// 统一授权跳转管理器
// 简化所有授权相关的跳转逻辑
// 直接使用环境变量，测试环境使用 /microform-test 路径
const BASE_PATH = window.location.pathname.startsWith('/microform-test') ? '/microform-test' : '';

class AuthRedirectManager {
  constructor() {
    this.isProcessing = false;
    this.currentRedirect = null;
  }

  // 检查是否正在处理跳转
  isRedirectProcessing() {
    return this.isProcessing || localStorage.getItem('isRedirectingToAuth') === 'true';
  }

  // 开始跳转流程
  startRedirect(type, returnUrl) {
    if (this.isRedirectProcessing()) {
      console.warn('⚠️ 跳转正在进行中，跳过重复跳转');
      return false;
    }

    this.isProcessing = true;
    this.currentRedirect = { type, returnUrl, timestamp: Date.now() };

    // 保存跳转信息
    localStorage.setItem('returnUrl', returnUrl);
    localStorage.setItem('isRedirectingToAuth', 'true');
    
    console.log(`🚀 开始${type}跳转流程:`, returnUrl);
    return true;
  }

  // 执行跳转到微信授权页面
  redirectToWechatAuth() {
    // const currentDomain = window.location.origin; // 原动态域名
    // const currentDomain = 'https://form.fangxiaojia.vip/microform-test'; // 临时测试域名
    const currentDomain = window.location.origin; // 动态获取当前域名
    // BASE_PATH 已在文件顶部导入
    const basePath = BASE_PATH || '';
    const authUrl = `${currentDomain}${basePath}/wechat-auth`;
    
    console.log('🚀 跳转到微信授权页面:', authUrl);
    
    try {
      window.location.replace(authUrl);
    } catch (error) {
      console.error('跳转失败，使用备用方式:', error);
      window.location.href = authUrl;
    }
  }

  // 构建授权后的跳转URL
  buildRedirectUrl(state) {
    let returnUrl = '/';
    
    // 优先使用保存的returnUrl
    const savedReturnUrl = localStorage.getItem('returnUrl');
    if (savedReturnUrl) {
      // 将 detail/xxx 转换为 display/xxx
      if (savedReturnUrl.includes('/detail/')) {
        returnUrl = savedReturnUrl.replace('/detail/', '/display/');
        console.log('🔄 转换detail为display页面:', returnUrl);
      } else {
        returnUrl = savedReturnUrl;
        console.log('🔄 使用保存的跳转URL:', returnUrl);
      }
    } else if (state && state.startsWith('detail/')) {
      // 使用state参数作为跳转URL，转换为display页面
      returnUrl = `/display/${state.replace('detail/', '')}`;
      console.log('🔄 使用state参数转换为display页面:', returnUrl);
    } else if (state === 'blackcard') {
      // 黑卡页面特殊处理
      returnUrl = '/black-card-activation-mobile';
      console.log('🔄 使用黑卡页面跳转URL:', returnUrl);
    } else if (state === 'sign_in_sheet') {
      // 签到页面特殊处理
      returnUrl = '/sign-in-sheet-form';
      console.log('🔄 使用签到页面跳转URL:', returnUrl);
    }
    
    // 添加跳转参数
    let finalUrl = returnUrl;
    try {
      const url = new URL(returnUrl, window.location.origin);
      url.searchParams.set('jump', '1');
      url.searchParams.set('auth_success', '1');
      url.searchParams.set('timestamp', Date.now().toString());
      finalUrl = url.toString();
    } catch (e) {
      // 兼容相对路径
      const separator = returnUrl.includes('?') ? '&' : '?';
      finalUrl = `${returnUrl}${separator}jump=1&auth_success=1&timestamp=${Date.now()}`;
    }
    
    console.log('🚀 最终跳转URL:', finalUrl);
    return finalUrl;
  }

  // 执行跳转回原页面
  executeRedirect(targetUrl, options = {}) {
    const { method = 'replace', delay = 0 } = options;
    
    const doRedirect = () => {
      try {
        if (method === 'replace') {
          window.location.replace(targetUrl);
        } else {
          window.location.href = targetUrl;
        }
        this.completeRedirect();
      } catch (error) {
        console.error('跳转失败:', error);
        // 备用跳转方式
        window.location.href = targetUrl;
        this.completeRedirect();
      }
    };

    if (delay > 0) {
      setTimeout(doRedirect, delay);
    } else {
      doRedirect();
    }
  }

  // 完成跳转
  completeRedirect() {
    this.isProcessing = false;
    this.currentRedirect = null;
    
    // 延迟清理，确保跳转完成
    setTimeout(() => {
      this.cleanup();
    }, 1000);
    
    console.log('✅ 跳转完成');
  }

  // 清理跳转数据
  cleanup() {
    try {
      localStorage.removeItem('returnUrl');
      localStorage.removeItem('wechatReturnUrl');
      localStorage.removeItem('isRedirectingToAuth');
      localStorage.removeItem('isRedirectingToWechat');
      localStorage.removeItem('isProcessingWechatAuth');
      localStorage.removeItem('wechatAuthCompleted');
      localStorage.removeItem('wechatAuthTimestamp');
      console.log('🧹 清理跳转数据完成');
    } catch (error) {
      console.error('清理跳转数据失败:', error);
    }
  }

  // 检查是否需要微信授权
  needsWechatAuth(currentPath) {
    return currentPath.includes('/detail') || currentPath.includes('/fill');
  }

  // 检查微信登录状态
  checkWechatLoginStatus() {
    const wechatUser = localStorage.getItem('wechatUser');
    const hasWechatNickname = wechatUser && JSON.parse(wechatUser)?.nickname;
    const isWechatLoggedIn = localStorage.getItem('isWechatLoggedIn') === '1';
    const isJump = new URLSearchParams(window.location.search).get('jump') === '1';
    const authSuccess = new URLSearchParams(window.location.search).get('auth_success') === '1';
    
    return {
      hasWechatNickname,
      isWechatLoggedIn,
      isJump,
      authSuccess,
      needsAuth: !hasWechatNickname && !isJump && !authSuccess
    };
  }
}

// 创建全局实例
const authRedirectManager = new AuthRedirectManager();

// 导出便捷方法
export const startAuthRedirect = (type, returnUrl) => 
  authRedirectManager.startRedirect(type, returnUrl);

export const redirectToWechatAuth = () => 
  authRedirectManager.redirectToWechatAuth();

export const buildAuthRedirectUrl = (state) => 
  authRedirectManager.buildRedirectUrl(state);

export const executeAuthRedirect = (targetUrl, options) => 
  authRedirectManager.executeRedirect(targetUrl, options);

export const completeAuthRedirect = () => 
  authRedirectManager.completeRedirect();

export const cleanupAuthRedirect = () => 
  authRedirectManager.cleanup();

export const isAuthRedirectProcessing = () => 
  authRedirectManager.isRedirectProcessing();

export const needsWechatAuth = (currentPath) => 
  authRedirectManager.needsWechatAuth(currentPath);

export const checkWechatLoginStatus = () => 
  authRedirectManager.checkWechatLoginStatus();

export default authRedirectManager;
